import Promise from 'bluebird';
import findIndex from 'lodash/findIndex';
import { BAM_PROMO_IFRAME_ID } from 'constants';
import { AdobePass, AuthenticatorType, AuthTypes, Entitlements, Flags, ModalType } from 'enums';
import { Authenticator, PreAuthZ, streamHasDTCPackage, streamHasAuthType, getContentId } from 'lib/auth';
import * as perf from 'lib/util/perf';
import * as untimelyLogoutTracker from 'lib/metrics/untimelyLogoutTracker';
import { ClientAPI } from 'lib/api';
import { dateFormat, getAdobePassRedirectUrl, addQueryToUrl } from 'lib/format';
import {
    trackMvpdPicker,
    trackProviderSelect,
    trackAuthenticationComplete,
    trackAuthorizationStart,
    trackAuthorizationComplete,
    clearTrackingData
} from 'lib/omniture';
import { isEmpty, getBamAuthConfig } from 'lib/util';
import * as oneId from 'lib/oneId';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import {
    adobePassInitializedSelector,
    adobePassAuthenticatedSelector,
    userAuthSelector,
    passOneIdTokenSelector
} from 'common/selectors/authenticationSelectors';
import { oneIdTokenSelector, oneIdAnalyticsSelector } from 'common/selectors/oneIdSelector';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import appSelector, {
    adobePassEnvSelector,
    countrySelector,
    isIntlSelector,
    packageFilterSelector,
    isDomesticSelector
} from 'common/selectors/appSelector';
import pageSelector from 'common/selectors/pageSelector';
import { persistentFlagsSelector } from 'common/selectors/flagsSelector';
import providerSelector, { providersSelector } from 'common/selectors/providerSelector';
import routingSelector from 'common/selectors/routingSelector';
import networksSelector from 'common/selectors/networksSelector';
import { navmethodSelector, entitlementsTrackingSelector } from 'common/selectors/analyticsSelectors';
import uniq from 'lodash/uniq';
import chunk from 'lodash/chunk';
import {
    contentSelect,
    contentUnSelect,
    streamSelect,
    streamUnSelect,
    savePackageId,
    savePackageTier,
    displayModalMessage,
    closeModalMessage
} from './appActions';
import { createFetchPageAction, setPageState } from 'common/page/actions';
import { fetchScheduleModule } from 'common/scheduleModule/actions';
import { scoreAll } from 'common/actions/scoringActions';
import { launchLogin } from 'common/actions/oneIdActions';
import { AccountSelectorComponent } from 'common/page/AccountSelector';
import WatchESPNWebPlayer from 'webapp/common/video-player';

export function createAuthenticator(type) {
    return (dispatch, getState) => {
        if (type === AuthenticatorType.IP) {
            return Authenticator.ip({ partner: 'watchespn', platform: 'web' });
        }
        return Authenticator.adobe({
            displayProviderDialogImpl: () => {},
            elementId: 'ae_container',
            environment: adobePassEnvSelector(getState()),
            redirectUrl: window.location.href,
            refreshless: true
        });
    };
}

// Reduxified Adobe Pass Actions
export const START_ADOBEPASS_INITIALIZE = 'START_ADOBEPASS_INITIALIZE';
export const COMPLETE_ADOBEPASS_INITIALIZE = 'COMPLETE_ADOBEPASS_INITIALIZE';
export const START_CHECK_AUTHENTICATION = 'START_CHECK_AUTHENTICATION';
export const COMPLETE_CHECK_AUTHENTICATION = 'COMPLETE_CHECK_AUTHENTICATION';
export const START_AUTHENTICATION = 'START_AUTHENTICATION';
export const COMPLETE_AUTHENTICATION = 'COMPLETE_AUTHENTICATION';
export const START_AUTHORIZATION = 'START_AUTHORIZATION';
export const COMPLETE_AUTHORIZATION = 'COMPLETE_AUTHORIZATION';
export const REQUEST_PROVIDERS = 'REQUEST_PROVIDERS';
export const RECEIVE_PROVIDERS = 'RECEIVE_PROVIDERS';
export const DISPLAY_PROVIDER_DIALOG = 'DISPLAY_PROVIDER_DIALOG';
export const CLOSE_PROVIDER_DIALOG = 'CLOSE_PROVIDER_DIALOG';
export const SELECT_PROVIDER = 'SELECT_PROVIDER';
export const START_LOGOUT = 'START_LOGOUT';
export const COMPLETE_LOGOUT = 'COMPLETE_LOGOUT';
export const GET_AUTHENTICATION_INFO = 'GET_AUTHENTICATION_INFO';
export const GET_METADATA = 'GET_METADATA';
export const COMPLETE_PREAUTH_CHECK = 'COMPLETE_PREAUTH_CHECK';
export const GET_LOCATION = 'GET_LOCATION';
export const ADOBEPASS_ERROR_EVENT = 'ADOBEPASS_ERROR_EVENT';

// BAM
export const START_CHECK_BAM_AUTHENTICATION = 'START_CHECK_BAM_AUTHENTICATION';
export const COMPLETE_CHECK_BAM_AUTHENTICATION = 'COMPLETE_CHECK_BAM_AUTHENTICATION';
export const GET_BAM_AUTHENTICATION_INFO = 'GET_BAM_AUTHENTICATION_INFO';
export const GET_GEOLOCATION = 'GET_GEOLOCATION';
export const GET_BAM_SESSION_TOKEN = 'GET_BAM_SESSION_TOKEN';
export const GET_USER_TRACKING = 'GET_USER_TRACKING';

// Reduxified IP actionsexport
export const START_CHECK_IP_AUTHENTICATION = 'START_CHECK_IP_AUTHENTICATION';
export const COMPLETE_CHECK_IP_AUTHENTICATION = 'COMPLETE_CHECK_IP_AUTHENTICATION';
export const GET_IP_AUTHENTICATION_INFO = 'GET_IP_AUTHENTICATION_INFO';

export function createGetBAMAuthenticationInfoAction(subscriptions) {
    return {
        type: GET_BAM_AUTHENTICATION_INFO,
        subscriptions
    };
}

export function createGetGeoLocationAction(geo) {
    return {
        type: GET_GEOLOCATION,
        geo
    };
}

export function createGetBAMSessionTokenAction(sessionToken) {
    return {
        type: GET_BAM_SESSION_TOKEN,
        session: {
            token: sessionToken,
            completeTime: Date.now()
        }
    };
}

export function createGetUserTrackingAction(userTracking) {
    return {
        type: GET_USER_TRACKING,
        userTracking
    };
}

export function createStartCheckBAMAuthenticationAction() {
    return {
        type: START_CHECK_BAM_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createCompleteCheckBAMAuthenticationAction() {
    return {
        type: COMPLETE_CHECK_BAM_AUTHENTICATION,
        completeTime: Date.now()
    };
}

// define ip authentication actions
export function createStartCheckIpAuthenticationAction() {
    return {
        type: START_CHECK_IP_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createCompleteCheckIpAuthenticationAction(isAuthenticated) {
    return {
        type: COMPLETE_CHECK_IP_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        authType: 'isp',
        startTime: Date.now()
    };
}

export function createGetIpAuthenticationInfoAction(info) {
    return {
        type: GET_IP_AUTHENTICATION_INFO,
        info
    };
}

export function createGetLocationAction(location) {
    return {
        type: GET_LOCATION,
        location
    };
}

export function createGetLocationFromUserDataAction() {
    return (dispatch) => (
        dispatch(createAuthenticator(AuthenticatorType.IP))
            .getLocation().then((location) => {
                if (location && typeof location === 'object') {
                    location.timeZone = dateFormat.getTimezone();
                    dispatch(createGetLocationAction(location));
                }
            })
    );
}

export function createReceiveProvidersAction(providers) {
    return {
        type: RECEIVE_PROVIDERS,
        providers,
        completeTime: Date.now()
    };
}


export function createRequestProvidersAction() {
    return {
        type: REQUEST_PROVIDERS,
        startTime: Date.now()
    };
}

const compareByName = (providerA, providerB) => (providerA.name.toLowerCase() > providerB.name.toLowerCase() ? 1 : -1);
export function getProviders({ country, partner, platform, version } = {}) {
    return (dispatch, getState) => {
        const state = getState();
        const opts = {
            partner,
            platform,
            version,
            country: country || countrySelector(state),
            passenv: adobePassEnvSelector(state)
        };
        const providersFromState = providersSelector(state);
        let providerPromise;
        dispatch(createRequestProvidersAction());
        if (providersFromState.length > 0) {
            providerPromise = Promise.resolve(providersFromState);
        } else {
            providerPromise = ClientAPI
                .getProviders(opts)
                .then((providersFromApi = []) => (providersFromApi.sort(compareByName)));
        }
        return providerPromise.then((providers) => {
            dispatch(createReceiveProvidersAction(providers));
            return providers;
        });
    };
}

export function createSmartIpAuthenticationAction() {
    return async (dispatch, getState) => {
        perf.markStart('createSmartIpAuthenticationAction');
        const ipAuthenticator = dispatch(createAuthenticator(AuthenticatorType.IP));

        await dispatch(createGetLocationFromUserDataAction());
        dispatch(createStartCheckIpAuthenticationAction());
        const isAuthenticated = await ipAuthenticator.checkAuthentication();
        perf.measureAndTime('createSmartIpAuthenticationAction');
        dispatch(createCompleteCheckIpAuthenticationAction(isAuthenticated));
        if (isAuthenticated === true) {
            const [providers, ipInfo] = await Promise.all([dispatch(getProviders()), ipAuthenticator.getAuthenticationInfo()]);
            let orderedProviders = providers;
            const providerIndex = findIndex(providers, (provider) => (provider.ispId === ipInfo.mvpd));

            // if the provider is not found in the provider list, check the legacy "watchespn-flash" client,
            // which contains ISP only affs that "watchespn-web" does not
            if (providerIndex === -1) {
                const country = countrySelector(getState());
                const provider = await ClientAPI.getProvider({ abbreviation: ipInfo.mvpd, country });
                if (provider) {
                    orderedProviders.unshift(provider);
                    dispatch(createReceiveProvidersAction(orderedProviders));
                    dispatch(createGetIpAuthenticationInfoAction(provider));
                }
                return provider;
            }
            const providerInfo = providers[providerIndex];
            if (providerIndex > 0) {
                providers.splice(providerIndex, 1); // Removes currIndex from providers array
                orderedProviders = [providerInfo, ...providers];
            }
            dispatch(createReceiveProvidersAction(orderedProviders));
            dispatch(createGetIpAuthenticationInfoAction(providerInfo));
            return providerInfo;
        }
        return null;
    };
}


// Define Adobe Pass Authentication Actions
export function createStartAdobePassInitializeAction() {
    return {
        type: START_ADOBEPASS_INITIALIZE,
        startTime: Date.now()
    };
}

export function createCompleteAdobePassInitializeAction() {
    return {
        type: COMPLETE_ADOBEPASS_INITIALIZE,
        completeTime: Date.now()
    };
}

// Define Adobe Pass Check Authentication Actions
export function createStartCheckAuthenticationAction() {
    return {
        type: START_CHECK_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createCompletePreauthCheckAction(resources = []) {
    return {
        type: COMPLETE_PREAUTH_CHECK,
        resources
    };
}

export function checkPreauthResources() {
    let resources = PreAuthZ.getResources();
    return async (dispatch, getState) => {
        const state = getState();
        const isIntl = isIntlSelector(state);
        const { adobePass: { preauthorizeNetworksEnabled, preauthorizeNetworks = [] } = {} } = bootstrapSelector(state);
        if (!isIntl && !preauthorizeNetworksEnabled) { // if preauth disabled for domestic, use static list of resources
            resources = preauthorizeNetworks;
        }
        if (!resources) {
            let allAdobeIds;
            const adobeAuthenticator = dispatch(createAuthenticator(AuthenticatorType.ADOBE));
            if (isIntl) {
                const networks = networksSelector(state) || [];
                allAdobeIds = uniq(networks
                        .filter(({ isActive, adobeResource }) => isActive && adobeResource)
                        .map(({ adobeResource }) => adobeResource));
                const chunkedAdobeIds = chunk(allAdobeIds, PreAuthZ.PREAUTH_MAX_LENGTH);
                // FIXME: it started to return the same preauthorized resources in the subsequent chunked requests..
                // https://tve.zendesk.com/hc/requests/34976
                /**
                 * There cannot be multiple "adobeAuthenticator.checkPreauthorizedResources" in flight since
                 * there is only one global function to handle the preauthz callback in `watch-authenticators-js`.
                 * For this reason, we await in a conventional for loop until each "checkPreauthorizedResources" completes
                 */
                let index = 0;
                for (index; index < chunkedAdobeIds.length; index += 1) {
                    const preAuthResources = await adobeAuthenticator.checkPreauthorizedResources(...chunkedAdobeIds[index]);
                    if (preAuthResources.length > 0) {
                        resources = (resources || []).concat(preAuthResources);
                    }
                }
                resources = uniq(resources);
            } else {
                const channels = getState().schedule.channels || [];
                allAdobeIds = uniq(channels.map((channel) => (channel.adobeResource)).filter(adobeResource => !!adobeResource));
                const chunkedAdobeIds = chunk(allAdobeIds, PreAuthZ.PREAUTH_MAX_LENGTH);
                // FIXME: it started to return the same preauthorized resources in the subsequent chunked requests..
                // https://tve.zendesk.com/hc/requests/34976
                /**
                 * There cannot be multiple "adobeAuthenticator.checkPreauthorizedResources" in flight since
                 * there is only one global function to handle the preauthz callback in `watch-authenticators-js`.
                 * For this reason, we await in a conventional for loop until each "checkPreauthorizedResources" completes
                 */
                let index = 0;
                for (index; index < chunkedAdobeIds.length; index += 1) {
                    const preAuthResources = await adobeAuthenticator.checkPreauthorizedResources(...chunkedAdobeIds[index]);
                    if (preAuthResources.length > 0) {
                        resources = (resources || []).concat(preAuthResources);
                    }
                }
                resources = uniq(resources);
                if (resources && resources.length) { // in case preauthz fails and return invalid data
                    PreAuthZ.setResources(resources, 1);    // set expiration to 1 day
                    if (
                        window.espn_ui && window.espn_ui.Helpers &&
                        window.espn_ui.Helpers.watchProviders && window.espn_ui.Helpers.watchProviders.setTVECookies
                    ) {
                        window.espn_ui.Helpers.watchProviders.setTVECookies(resources);
                    }
                }
            }
        }

        dispatch(createCompletePreauthCheckAction(resources));
        return resources;
    };
}

export function createCompleteCheckAuthenticationAction(isAuthenticated) {
    return {
        type: COMPLETE_CHECK_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        isAuthenticated,
        authType: AuthTypes.MVPD,
        completeTime: Date.now()
    };
}

export function createStartAuthenticationAction(content) {
    return {
        type: START_AUTHENTICATION,
        content,
        startTime: Date.now()
    };
}

export function createCompleteAuthenticationAction(isAuthenticated, mvpd) {
    return {
        type: COMPLETE_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        isAuthenticated,
        authType: AuthTypes.MVPD,
        mvpd,
        completeTime: Date.now()
    };
}

export function createStartAuthorizationAction(resourceId) {
    return {
        type: START_AUTHORIZATION,
        resourceId,
        startTime: Date.now()
    };
}

export function createCompleteAuthorizationAction(resourceId, token) {
    return {
        type: COMPLETE_AUTHORIZATION,
        resourceId,
        token,
        completeTime: Date.now()
    };
}

export function createAdobePassErrorAction(error) {
    return {
        type: ADOBEPASS_ERROR_EVENT,
        error
    };
}

// Provider Display and Select Action creators.
// The Display Provider Dialog action, which is triggered by startAuthentication
export function createDisplayProviderDialogAction() {
    return (dispatch, getState) => {
        const state = getState();
        trackMvpdPicker({ isDomestic: isDomesticSelector(state) });
        dispatch({
            type: DISPLAY_PROVIDER_DIALOG,
            showProviderDialog: true
        });
    };
}

export function createCloseProviderDialogAction(resume = true) {
    return (dispatch, getState) => {
        dispatch({
            type: CLOSE_PROVIDER_DIALOG,
            showProviderDialog: false
        });
        if (resume) {
            const { previousContent, playingStream } = appSelector(getState());
            if (!isEmpty(playingStream)) {
                dispatch(contentUnSelect());
                dispatch(contentSelect(previousContent));
            }
        }
    };
}

export function createStartLogoutAction() {
    return {
        type: START_LOGOUT,
        startTime: Date.now()
    };
}

export function createCompleteLogoutAction() {
    PreAuthZ.clear();

    return {
        type: COMPLETE_LOGOUT,
        directive: 'remove',
        isAuthenticated: false,
        authType: AuthTypes.MVPD,
        completeTime: Date.now()
    };
}

export function createAndPerformLogoutAction(stream) {
    return (dispatch, getState) => {
        dispatch(createStartLogoutAction());
        PreAuthZ.clear();
        if (
            window.espn_ui && window.espn_ui.Helpers &&
            window.espn_ui.Helpers.watchProviders && window.espn_ui.Helpers.watchProviders.setTVECookies
        ) {
            window.espn_ui.Helpers.watchProviders.removeTVECookies();
        }
        return dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .deauthenticate().then((isAuthenticated) => {
                // purposefully logged out, forget their authn ttl so we don't track a discrepancy
                untimelyLogoutTracker.reset();
                if (isAuthenticated === false) {
                    dispatch(createCompleteLogoutAction());
                    if (stream && stream.authTypes) {
                        const content = selectedContentSelector(getState());
                        content.reEmbed = true;
                        dispatch(streamUnSelect());
                        dispatch(streamSelect(stream));
                    }
                }
                return Promise.resolve();
            });
    };
}

export function createGetAuthenticationInfoAction(info) {
    return (dispatch, getState) => {
        const state = getState();
        const userAuth = userAuthSelector(state);
        const isDomestic = isDomesticSelector(state);
        const entitlementsTracking = entitlementsTrackingSelector(state);
        trackAuthenticationComplete({ userAuth, provider: info, entitlementsTracking, isDomestic });
        dispatch({
            type: GET_AUTHENTICATION_INFO,
            info
        });
    };
}

export function createGetMetadataAction(key, value) {
    return {
        type: GET_METADATA,
        key,
        value
    };
}

export function listenForAdobePassErrorEvent() {
    return (dispatch) => dispatch(createAuthenticator(AuthenticatorType.ADOBE))
        .onErrorEvent((errorData) => {
            dispatch(createAdobePassErrorAction(errorData));
        });
}

export function initializeAdobePassAuthenticator() {
    return (dispatch) => {
        dispatch(createStartAdobePassInitializeAction());
        dispatch(listenForAdobePassErrorEvent());
        return dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .initialize().then((initialized) => {
                if (initialized) {
                    dispatch(createCompleteAdobePassInitializeAction());
                }
                return initialized;
            });
    };
}

export function checkAdobePassAuthentication() {
    return (dispatch, getState) => {
        const adobePassInitialized = adobePassInitializedSelector(getState());
        if (!adobePassInitialized) {
            return Promise.resolve(false);
        }
        dispatch(createStartCheckAuthenticationAction());
        return dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .checkAuthentication()
            .then((isAuthenticated) => {
                dispatch(createCompleteCheckAuthenticationAction(isAuthenticated));
                return isAuthenticated;
            });
    };
}

export function getAdobePassAuthenticationInfo() {
    return (dispatch) => dispatch(createAuthenticator(AuthenticatorType.ADOBE))
        .getAuthenticationInfo();
}

export function getAdobePassMetadata(key) {
    return (dispatch) => dispatch(createAuthenticator(AuthenticatorType.ADOBE))
        .getMetadata(key);
}

export function createAsyncGetMetadataAction(key) {
    return (dispatch) => dispatch(getAdobePassMetadata(key))
        .then((value = `${Math.random()}`) => {
            dispatch(createGetMetadataAction(key, value));
            return Promise.resolve(value);
        }, (error) => Promise.reject(error));
}

// Initializes Adobe, checks authentication, then gets provider list if not authenticated.
// Called on page load
export function createSmartAdobePassInitializeAction(routerParams) {
    return async (dispatch, getState) => {
        perf.markStart('createSmartAdobePassInitializeAction');
        const isIntl = isIntlSelector(getState());
        if (!isIntl) {
            // check and store preauthz resource before awaiting async functions
            const preAuthResources = PreAuthZ.getResources();
            if (preAuthResources) {
                dispatch(createCompletePreauthCheckAction(preAuthResources));
            }
        }
        await dispatch(initializeAdobePassAuthenticator());
        await dispatch(checkAdobePassAuthentication());
        await dispatch(getProviders());
        // make sure to grab from `getState()` not from cached one
        const state = getState();
        const isAuthenticated = adobePassAuthenticatedSelector(state);
        if (isAuthenticated) {
            const info = await dispatch(getAdobePassAuthenticationInfo());
            // just to send `usid` to page view tracking..
            await dispatch(createAsyncGetMetadataAction(AdobePass.METADATA.UPSTREAM_USERID));
            const { providerDialog: { providers: providersFromApi } } = state;
            const providerInfo = providersFromApi.filter(({ abbreviation }) =>
                abbreviation === info.mvpd
            ).shift();
            // this means you are authenticated for mvpd in a different country
            // if we swallow it, provider dialog won't show up for the current country and users get stuck,
            // so logging out from the current invalid mvpd
            if (!providerInfo) {
                await dispatch(createAndPerformLogoutAction());
            }

            const { lang } = appSelector(state);
            const { locationBeforeTransitions } = routingSelector(state);
            const { id: pageId } = pageSelector(state);
            const { query = {} } = locationBeforeTransitions || {};

            if (isIntl) {
                const packageId = await dispatch(getAdobePassMetadata(AdobePass.METADATA.PACKAGE_ID));
                let packageTier = await dispatch(getAdobePassMetadata(AdobePass.METADATA.PACKAGE_TIER));
                // consumerKey from the affiliateApi is the package ID.
                const pkg = providerInfo.packages.filter(item => item.consumerKey === packageId).shift();
                if (!packageTier && pkg) {
                    // packageId from the affiliateApi contains package tier info.
                    packageTier = pkg.packageId;
                }
                dispatch(createGetMetadataAction(AdobePass.METADATA.PACKAGE_ID, packageId));
                dispatch(createGetMetadataAction(AdobePass.METADATA.PACKAGE_TIER, packageTier));
                dispatch(createGetAuthenticationInfoAction({ ...providerInfo, package: pkg }));
                dispatch(savePackageId(packageId));
                dispatch(savePackageTier(packageTier));

                const packageFilter = packageFilterSelector(getState());
                if (packageFilter && pageId !== 'syndicated-player') {
                    await Promise.all([
                        dispatch(createFetchPageAction(pageId, routerParams, query, lang)),
                        dispatch(fetchScheduleModule(lang))
                    ]);
                    dispatch(scoreAll());
                } else {
                    dispatch(setPageState(true));
                }
            } else {
                dispatch(createGetAuthenticationInfoAction(providerInfo));
            }

            // save the authn ttl to local storage so we can track a discrepancy later
            if (!untimelyLogoutTracker.isAuthNTtlSaved() && providerInfo) {
                untimelyLogoutTracker.saveInitialAuthNData(providerInfo);
                dispatch(getAdobePassMetadata(AdobePass.METADATA.TTL_AUTHN))
                    .then((ttl) => {
                        untimelyLogoutTracker.saveAuthNTtl(ttl);
                    });
            }

            if (isIntl) {
                // TODO: add `fetchNetwork` back in PageShell.js and enable this once we are comfortable
                /* const { location: { path = '' } } = appSelector(getState());
                // only check preAuth for schedule page
                if (path.indexOf('schedule') !== -1 && providerInfo) {
                    dispatch(checkPreauthResources());
                } */
            } else {
                // not awaiting on purpose based on the requirement, it's ok to reflect the `preAuthResources` next time
                dispatch(checkPreauthResources());
            }
        }
        if (isIntl) {
            dispatch(setPageState(true));
        }
        untimelyLogoutTracker.trackUntimelyLogout(entitlementsTrackingSelector(getState()));
        perf.measureAndTime('createSmartAdobePassInitializeAction');
        return null;
    };
}

export function createAndPerformStartAuthenticationAction({ content, stream, redirectUrl: redirectUrlArg } = {}) {
    return (dispatch, getState) => {
        const flags = persistentFlagsSelector(getState());
        const { locationBeforeTransitions } = routingSelector(getState());
        const { query = {}, pathname = '' } = locationBeforeTransitions || {};
        const redirectUrl = redirectUrlArg || getAdobePassRedirectUrl({ content, stream, flags, query, pathname });
        dispatch(createStartAuthenticationAction(content));
        return dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .authenticate(null, null, redirectUrl || '')
            .then((isAuthenticated) => {
                dispatch(createCompleteAuthenticationAction(isAuthenticated));
                return Promise.resolve(isAuthenticated);
            });
    };
}

export function createAndPerformStartAuthorizationAction(resource) {
    return (dispatch, getState) => {
        dispatch(createStartAuthorizationAction(resource));
        const state = getState();
        trackAuthorizationStart({
            userAuth: userAuthSelector(state),
            provider: providerSelector(state),
            entitlementsTracking: entitlementsTrackingSelector(state),
            isDomestic: isDomesticSelector(state)
        });

        return dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .authorize(resource)
            .then((response) => {
                dispatch(createCompleteAuthorizationAction(response.resourceId, response.token));
                trackAuthorizationComplete({
                    userAuth: userAuthSelector(state),
                    provider: providerSelector(state),
                    entitlementsTracking: entitlementsTrackingSelector(state),
                    isDomestic: isDomesticSelector(state)
                });
                if (!response.errorCode && !response.errorDetails) {
                    return Promise.resolve({
                        resourceId: response.resourceId,
                        token: response.token
                    });
                }
                return Promise.reject(new Error(response.errorDetails || response.errorCode));
            })
            .catch(error => {
                dispatch(createCompleteAuthorizationAction());
                return Promise.reject(error);
            });
    };
}

export function createSelectProviderAction(provider) {
    return (dispatch, getState) => {
        const adobeProvider = {
            ID: (provider && provider.ID) || provider.abbreviation
        };

        if (adobeProvider.ID) {
            const state = getState();
            const isDomestic = isDomesticSelector(state);
            trackProviderSelect({
                userAuth: userAuthSelector(state),
                entitlementsTracking: entitlementsTrackingSelector(state),
                provider,
                isDomestic
            });
        } else {
            clearTrackingData();
        }
        dispatch(createAuthenticator(AuthenticatorType.ADOBE))
            .selectProvider(adobeProvider).then((isAuthenticated) => {
                // this probably won't ever get called
                dispatch(createCompleteAuthenticationAction(isAuthenticated, adobeProvider.ID));
            });
    };
}

export function createAccountSelectorAction(content, redirectUrlArg) {
    return (dispatch, getState) => {
        const state = getState();
        const flags = persistentFlagsSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        const { query = {} } = locationBeforeTransitions || {};
        const redirectUrl = redirectUrlArg || getAdobePassRedirectUrl({ content, flags, query });
        const mode = AccountSelectorComponent.MODE.ACCOUNT_SELECTOR;
        const oneIdParam = query[Flags.ONEID_ENV] ? `${Flags.ONEID_ENV}=${query[Flags.ONEID_ENV]}&` : '';
        const url = `/watch/account-selector?${oneIdParam}mode=${mode}&redirectUrl=${encodeURIComponent(redirectUrl)}`;
        window.location.href = url;
    };
}

export function createDTCPaywallAction({ content, stream, containerId }) {
    return async (dispatch, getState) => {
        const state = getState();
        const { bam = {} } = bootstrapSelector(state);
        const passOneIdToken = passOneIdTokenSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        const { query, pathname } = locationBeforeTransitions || {};
        const flags = persistentFlagsSelector(state);
        const oneIdToken = oneIdTokenSelector(state);
        const analytics = oneIdAnalyticsSelector(state) || {};
        const userAuth = userAuthSelector(state);
        const redirectUrl = getAdobePassRedirectUrl({ content, stream, flags, query, pathname });
        const isPromo = containerId === BAM_PROMO_IFRAME_ID;
        const navmethod = navmethodSelector(state);
        if (query[Flags.PAYWALL] === 'false') {
            if (!isPromo && !stream) {
                console.error('Empty stream selected');
                return;
            }
            if (!isPromo) {
                const hasMLB = streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_MLB);
                // TODO: DTC - find out how to detect `vertical` w/o checking particular package
                const isVertical = hasMLB || streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_NHL);
                const isMatchup = !!content.event; // TODO: DTC - confirm this check is correct
                let matchup;
                if (!isVertical && isMatchup) {
                    matchup = true;
                }
                const matchupParam = matchup ? `matchup=${matchup}&` : '';
                // const oneIdParam = query[Flags.ONEID_ENV] ? `${Flags.ONEID_ENV}=${query[Flags.ONEID_ENV]}&` : '';
                const oneIdParam = query[Flags.DISNEY_ENV] === 'stg' ? `${Flags.ONEID_ENV}=staging&` : '';
                const idParam = `id=${stream.id}`;
                const redirectParam = `redirectUrl=${encodeURIComponent(redirectUrl)}`;
                const url = `/watch/account-selector?${oneIdParam}${matchupParam}${idParam}&${redirectParam}`;
                dispatch(displayModalMessage(ModalType.PAYWALL, { src: url }));
            } else {
                const oneIdParam = query[Flags.DISNEY_ENV] === 'stg' ? `${Flags.ONEID_ENV}=staging&` : '';
                const idParam = `id=${content.id}`;
                const redirectParam = `redirectUrl=${encodeURIComponent(redirectUrl)}`;
                const url = `/watch/account-selector?${oneIdParam}${idParam}&${redirectParam}`;
                const promoEl = document.getElementById(BAM_PROMO_IFRAME_ID);
                if (promoEl) {
                    promoEl.innerHTML = `<iframe src="${url}"></iframe>`;
                }
            }
        } else {
            if (!isPromo) {
                dispatch(displayModalMessage(ModalType.PAYWALL, { content }));
            }
            const videoPlayer = new WatchESPNWebPlayer({
                bamPlayerOptions: query[Flags.BAM_ENV] ? { ...bam, env: query[Flags.BAM_ENV] } : bam
            });
            const contentId = getContentId(content);
            const eventIdOrId = contentId.eventId || contentId.id;
            const paywallConfig = {
                container: `#${containerId}`,
                customProps: {},
                ...getBamAuthConfig(oneIdToken, passOneIdToken, userAuth),
                redirectUrl,
                isPromo,
                analytics
            };

            // Only assign navmethod to analytics object if defined
            if (typeof navmethod === 'string' && navmethod !== '') {
                paywallConfig.analytics.navmethod = navmethod;
            }

            let idKey = 'id';
            if (!stream && contentId.eventId) {
                idKey = 'eventId';
            }

            if (!isPromo) {
                if (stream) {
                    paywallConfig[idKey] = stream.id;
                    /**
                     * Set stream to `customProps` where DSS needs to handle paywall and playback
                     * To handle the /schedule page which uses GraphQL, we must embed
                     * the `content` object which has been updated to contain the `backgroundImageHref`
                     * 1. var isContentInStream: check if stream has content props, if it is yes, use stream
                     * 2. if isContentInStream returns false, get firstStream (stream[0]) from content
                     * 3. if step 1 and step 2 fail, let's just pass content object along with stream
                     */
                    const isContentInStream = stream && stream.content;
                    const firstStream = content.streams[0];
                    let streamObject;
                    if (isContentInStream) {
                        streamObject = stream;
                    } else if (firstStream && firstStream.content) {
                        streamObject = firstStream;
                    } else {
                        streamObject = { ...stream, content };
                    }
                    paywallConfig.customProps.stream = streamObject;
                } else {
                    paywallConfig[idKey] = eventIdOrId;
                    // NOTE: In a multi-stream scenario we need to pass the event content to `customProps`
                    // to ensure the generic paywall image is rendered.
                    // The solution is to use the first stream which contains the `backgroundImageHref` as primary
                    // and fallback to nesting the parent object with the content
                    if (content) {
                        const firstStream = content.streams[0];
                        paywallConfig.customProps.stream = firstStream || { ...content, content };
                    }
                }
            }

            if (query[Flags.DISNEY_ENV] === 'stg') {
                paywallConfig.oneIdEnv = 'stg';
            }
            if (!isPromo) {
                videoPlayer.once(WatchESPNWebPlayer.Events.LOGIN_REQUESTED, (event) => {
                    const { success, body: { authType, redirectUrl: redirectUrlArg } = {} } = event;
                    if (!success) {
                        console.error('LOGIN_REQUESTED', event);
                    }
                    dispatch(closeModalMessage());
                    if (authType === AuthTypes.BAM) {
                        oneId.on('login', () => {
                            let streamArg = stream;
                            if (!streamArg) {
                                streamArg = content.streams
                                    .filter((streamParam) => streamHasAuthType(streamParam, AuthTypes.BAM))[0];
                            }
                            const loginQuery = {
                                [`login_with_content_${idKey}`]: eventIdOrId,
                                login_with_pkg: streamArg && streamArg.packages && streamArg.packages.join(',')
                            };
                            // pass stream id only when it's different from content id
                            if (streamArg && streamArg.id !== eventIdOrId) {
                                loginQuery.login_with_id = streamArg.id;
                            }
                            // pass airingId that user selected from picker to login params
                            // so that we know which stream to play in the player
                            const { airingId } = query;
                            if (airingId) {
                                loginQuery.airingId = airingId;
                            }
                            window.location.href = addQueryToUrl(redirectUrlArg, loginQuery);
                        });
                        dispatch(launchLogin());
                    } else if (authType === AuthTypes.MVPD) {
                        dispatch(createAndPerformStartAuthenticationAction({ content, stream }))
                            .then((isAuthenticated) => {
                                if (!isAuthenticated) {
                                    dispatch(createDisplayProviderDialogAction());
                                }
                            });
                    }
                });
            }
            try {
                console.log('paywallConfig', paywallConfig); // eslint-disable-line no-console
                await videoPlayer.renderPaywall(paywallConfig);
            } catch (err) {
                console.error(err);
            }
        }
    };
}



// WEBPACK FOOTER //
// ./app/common/actions/authenticationActions.js